// 📄 hooks/useSolutionStats.ts
// Estadísticas de UNA solución (commerce, slp, sourcing, contracts, ...)
// para la pestaña correspondiente del ProjectStatsPanel.
//
// - Progreso por canal: Commerce reutiliza useAllChannelsProgress (misma
//   lectura de Firestore que ya usan las cards de CommerceHome.tsx). SLP,
//   Sourcing y Contracts reutilizan useGenericSolutionProgress, que lee la
//   misma estructura que usa el checklist real de cada canal genérico (ver
//   useCanalIntegracion.ts / solucionIntegrationService.ts). Buying y CIG
//   no tienen canales definidos en integrationChannels.ts todavía, así que
//   `disponible` queda en false para esas — no se inventan datos.
// - SR / Issues: los Incidentes SÍ tienen un campo real `solucionRelacionada`
//   (ver IncidenteFormModal.tsx) que aplica a cualquier solución, así que
//   esos contadores funcionan para todas — se recalculan con cada solutionId.
import { useEffect, useMemo, useState } from 'react';
import { useAllChannelsProgress, ChannelProgress } from './useAllChannelsProgress.ts';
import { useGenericSolutionProgress } from './useGenericSolutionProgress.ts';
import { getIncidentes } from '../services/incidenteService.ts';
import { SOLUTION_CHANNELS, SolutionChannelDef } from '../data/solutionChannelsMap.ts';
import { SolucionConCanales } from '../pages/Proyectos/integrationChannels.ts';

const SOLUCIONES_GENERICAS: SolucionConCanales[] = ['slp', 'sourcing', 'contracts'];

export interface SolutionChannelStat {
  id: string;
  nombre: string;
  completados: number;
  total: number;
  lastCompleted: string | null;
}

export interface SolutionStats {
  loading: boolean;
  error: string | null;
  disponible: boolean;
  totalPasos: number;
  pasosCompletados: number;
  porcentaje: number;
  canales: SolutionChannelStat[];
  canalesConfigurados: number;
  totalCanales: number;
  srCount: number;
  issuesCount: number;
}

const EMPTY_CHANNELS: SolutionChannelDef[] = [];

export function useSolutionStats(projectId: string, solutionId: string): SolutionStats {
  const channelDefs = SOLUTION_CHANNELS[solutionId] || EMPTY_CHANNELS;
  const disponible = channelDefs.length > 0;
  const esCommerce = solutionId === 'commerce';
  const esGenerica = SOLUCIONES_GENERICAS.includes(solutionId as SolucionConCanales);

  const commerceResult = useAllChannelsProgress(esCommerce ? projectId : '');
  const genericResult = useGenericSolutionProgress(esGenerica ? projectId : '', esGenerica ? (solutionId as SolucionConCanales) : null);

  const { loading: channelsLoading, error: channelsError, progress: channelsProgress } = esCommerce ? commerceResult : genericResult;

  const [incidentesLoading, setIncidentesLoading] = useState(true);
  const [incidentesError, setIncidentesError] = useState<string | null>(null);
  const [srCount, setSrCount] = useState(0);
  const [issuesCount, setIssuesCount] = useState(0);

  useEffect(() => {
    if (!projectId || !solutionId) {
      setIncidentesLoading(false);
      return;
    }

    let cancelado = false;

    (async () => {
      setIncidentesLoading(true);
      setIncidentesError(null);
      try {
        const incidentes = await getIncidentes(projectId);
        if (cancelado) return;
        const deLaSolucion = incidentes.filter((i) => i.solucionRelacionada === solutionId);
        setSrCount(deLaSolucion.filter((i) => !!i.categoriaSR).length);
        setIssuesCount(deLaSolucion.filter((i) => i.estado === 'Abierto').length);
      } catch (err) {
        console.error('Error cargando indicadores de la solución:', err);
        if (!cancelado) setIncidentesError('No se pudieron cargar los indicadores de la solución.');
      } finally {
        if (!cancelado) setIncidentesLoading(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [projectId, solutionId]);

  return useMemo(() => {
    const canales: SolutionChannelStat[] = channelDefs.map((def) => {
      const p: ChannelProgress | undefined = channelsProgress[def.id];
      return {
        id: def.id,
        nombre: def.nombre,
        completados: p?.completed || 0,
        total: p?.total || 0,
        lastCompleted: p?.lastCompleted || null
      };
    });

    const totalPasos = canales.reduce((sum, c) => sum + c.total, 0);
    const pasosCompletados = canales.reduce((sum, c) => sum + c.completados, 0);
    const porcentaje = totalPasos > 0 ? Math.round((pasosCompletados / totalPasos) * 100) : 0;
    const canalesConfigurados = canales.filter((c) => c.completados > 0).length;

    return {
      loading: (disponible && channelsLoading) || incidentesLoading,
      error: channelsError || incidentesError,
      disponible,
      totalPasos,
      pasosCompletados,
      porcentaje,
      canales,
      canalesConfigurados,
      totalCanales: canales.length,
      srCount,
      issuesCount
    };
  }, [channelDefs, disponible, channelsLoading, channelsError, channelsProgress, incidentesLoading, incidentesError, srCount, issuesCount]);
}
